import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

import { CuttingMatBaseGroup } from '../../models/cutting/cutting-material-group.model';
import { MatBaseGroupDiscount } from '../../models/cutting/mat-base-group-discount.model';

type GroupWithDiscount = CuttingMatBaseGroup & MatBaseGroupDiscount;

@Component({
  selector: 'csl-group-matrial-discount-tree',
  templateUrl: './cutting-group-material-discount-tree.component.html',
  styleUrls: ['./cutting-group-material-discount-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GroupMaterialDiscountTreeComponent implements OnInit, OnDestroy, OnChanges {
  discountChange$: Subject<GroupWithDiscount> = new Subject();

  unsubscribe$: Subject<void> = new Subject();

  @Input() groupMaterials: CuttingMatBaseGroup[] = [];
  @Input() groupMaterialsDiscounts: MatBaseGroupDiscount[] = [];
  @Input() readonly = false;

  @ViewChild('groupsDiscountForm') groupsDiscountForm: NgForm;

  @Output() discountChange: Subject<MatBaseGroupDiscount[]> = new Subject();

  groups: GroupWithDiscount[] = [];

  treeControl = new NestedTreeControl<GroupWithDiscount>(node => node.children as GroupWithDiscount[]);
  dataSource = new MatTreeNestedDataSource<GroupWithDiscount>();
  levels: Map<GroupWithDiscount, number> = new Map<GroupWithDiscount, number>();

  hasChild = (n: number, node: GroupWithDiscount) => !!node.children && node.children.length > 0;

  constructor() {}

  ngOnInit(): void {
    this.discountChange$
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(200),
        tap(g => {
          if (g.children) {
            this.setDiscountValueForTree(g.children as GroupWithDiscount[], g.discountPct);
          }

          this.outputData();
        })
      )
      .subscribe();
  }

  ngOnChanges(): void {
    this.loadData();
  }

  private outputData(): void {
    if (this.groupsDiscountForm.valid) {
      this.discountChange.next(this.getDiascounts(this.groups));
    } else {
      this.discountChange.next(undefined);
    }
  }

  private getDiascounts(groups: GroupWithDiscount[]): MatBaseGroupDiscount[] {
    return _.flatMap(groups, g => [
      {
        inMatBaseId: g.id,
        discountPct: g.discountPct,
      } as MatBaseGroupDiscount,
      ...this.getDiascounts((g.children as GroupWithDiscount[]) || []),
    ]).filter(g => !_.isNil(g.discountPct) && g.discountPct > 0);
  }

  private loadData(): void {
    // this.groups = _.cloneDeep(this.groupMaterials) as GroupWithDiscount[];
    this.groups = this.groupMaterials as GroupWithDiscount[];

    if (!_.isNil(this.groupMaterialsDiscounts) && this.groups.length > 0) {
      this.setDiscountForTree(this.groups, this.groupMaterialsDiscounts);
    }

    this.fillTree();
  }

  private setDiscountValueForTree(groups: GroupWithDiscount[], value: number): void {
    groups.forEach(g => {
      g.discountPct = value;

      if (g.children) {
        this.setDiscountValueForTree(g.children as GroupWithDiscount[], value);
      }
    });
  }

  private setDiscountForTree(groups: GroupWithDiscount[], groupMaterialsDiscounts: MatBaseGroupDiscount[]): void {
    const discounts = _.cloneDeep(groupMaterialsDiscounts);

    groups.forEach(g => {
      const discountIndex = discounts.findIndex(d => d.inMatBaseId === g.id);

      if (discountIndex >= 0) {
        g.discountPct = discounts[discountIndex].discountPct;
        discounts.splice(discountIndex, 1);
      } else {
        g.discountPct = 0;
      }

      if (g.children) {
        this.setDiscountForTree(g.children as GroupWithDiscount[], discounts);
      }
    });
  }

  fillTree(): void {
    this.dataSource.data = this.groups;

    this.levels.clear();
    for (const node of this.groups) {
      this.loadLevel(node, 0);
    }
  }

  getLevel(node: GroupWithDiscount): number {
    return this.levels.get(node);
  }

  loadLevel(item: GroupWithDiscount, level: number): void {
    this.levels.set(item, level);

    if (item.children && item.children.length > 0) {
      for (const childItem of item.children) {
        this.loadLevel(childItem as GroupWithDiscount, level + 1);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
